import './index.css'
import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'

interface InviteInfo {
  inviterName: string
  inviterEmail: string
  expiresAt: string
}

interface InviteAcceptScreenProps {
  token: string
  isAuthenticated: boolean
  autoAccept: boolean
  onNeedsAuth: () => void
  onAccepted: (newHouseholdId: string) => void
  onDeclined: () => void
}

export default function InviteAcceptScreen({
  token,
  isAuthenticated,
  autoAccept,
  onNeedsAuth,
  onAccepted,
  onDeclined,
}: InviteAcceptScreenProps) {
  const [inviteInfo,  setInviteInfo]  = useState<InviteInfo | null>(null)
  const [loadError,   setLoadError]   = useState<string | null>(null)
  const [loading,     setLoading]     = useState(true)
  const [acting,      setActing]      = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [autoAcceptTriggered, setAutoAcceptTriggered] = useState(false)

  useEffect(() => {
    async function validateToken() {
      setLoading(true)
      setLoadError(null)
      try {
        // get_invite_preview is a SECURITY DEFINER function that returns safe fields
        // without exposing inviter_id or invitee_email — the token itself is the access key.
        const { data, error } = await supabase
          .rpc('get_invite_preview', { p_token: token })

        const row = Array.isArray(data) ? data[0] : null

        if (error || !row) {
          setLoadError('Convite não encontrado.')
          return
        }

        if (row.status !== 'pending') {
          setLoadError('Este convite já foi utilizado ou expirou.')
          return
        }

        if (new Date(row.expires_at) < new Date()) {
          setLoadError('Este convite expirou. Peça um novo convite.')
          return
        }

        setInviteInfo({
          inviterName: row.household_name ?? 'Seu parceiro',
          inviterEmail: '',
          expiresAt: row.expires_at,
        })
      } catch {
        setLoadError('Erro ao carregar o convite.')
      } finally {
        setLoading(false)
      }
    }

    validateToken()
  }, [token])

  async function handleAction(action: 'accept' | 'decline') {
    // Decline without login: just navigate away — invite expires on its own.
    if (action === 'decline' && !isAuthenticated) {
      onDeclined()
      return
    }

    // Accept without login: bounce to AuthScreen; we'll resume after sign-in.
    if (action === 'accept' && !isAuthenticated) {
      onNeedsAuth()
      return
    }

    setActing(true)
    setActionError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const { data, error: fnErr } = await supabase.functions.invoke('accept-couple-invite', {
        body: { token, action },
        headers: { Authorization: `Bearer ${session?.access_token}` },
      })

      if (fnErr) {
        const body = await (fnErr as { context?: Response }).context?.json?.().catch(() => null)
        throw new Error(body?.error ?? fnErr.message)
      }

      const result = data as { ok: boolean; action: string; householdId?: string }

      if (action === 'decline') {
        onDeclined()
        return
      }

      if (result.householdId) {
        onAccepted(result.householdId)
      }
    } catch (err) {
      setActionError((err as Error).message ?? 'Erro ao processar convite.')
    } finally {
      setActing(false)
    }
  }

  // After the user signs in following an Accept click, auto-resume the accept flow.
  useEffect(() => {
    if (!autoAccept || autoAcceptTriggered) return
    if (loading || loadError || !inviteInfo || !isAuthenticated) return
    setAutoAcceptTriggered(true)
    handleAction('accept')
  }, [autoAccept, autoAcceptTriggered, loading, loadError, inviteInfo, isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  function daysUntil(isoDate: string): number {
    const diff = new Date(isoDate).getTime() - Date.now()
    return Math.max(0, Math.ceil(diff / 86_400_000))
  }

  return (
    <div className="bc-invite-accept">
      <div className="bc-invite-accept__card">
        <div className="bc-invite-accept__logo">
          <img src="/logo-mark.svg" width="40" height="40" alt="" />
          <span className="bc-invite-accept__brand">BagCash</span>
        </div>

        {loading && (
          <div className="bc-invite-accept__body">
            <p className="bc-invite-accept__desc">Validando convite…</p>
          </div>
        )}

        {!loading && loadError && (
          <div className="bc-invite-accept__body">
            <p className="bc-invite-accept__error">{loadError}</p>
            <button
              className="bc-btn bc-btn--secondary"
              onClick={onDeclined}
              style={{ marginTop: '1rem' }}
            >
              Ir para o início
            </button>
          </div>
        )}

        {!loading && !loadError && inviteInfo && (
          <div className="bc-invite-accept__body">
            <h1 className="bc-invite-accept__title">Convite para Conta Casal</h1>
            <p className="bc-invite-accept__desc">
              <strong>{inviteInfo.inviterName}</strong> te convidou para compartilhar finanças juntos no BagCash.
              Ao aceitar, vocês passam a ver e lançar dados em tempo real.
            </p>
            <p className="bc-invite-accept__expiry">
              Este convite expira em {daysUntil(inviteInfo.expiresAt)} dia(s).
            </p>

            {actionError && (
              <p className="bc-invite-accept__error">{actionError}</p>
            )}

            <div className="bc-invite-accept__actions">
              <button
                className="bc-btn bc-btn--primary"
                onClick={() => handleAction('accept')}
                disabled={acting}
              >
                {acting ? 'Processando…' : 'Aceitar convite'}
              </button>
              <button
                className="bc-btn bc-btn--secondary"
                onClick={() => handleAction('decline')}
                disabled={acting}
              >
                Recusar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
